import type { User } from '#shared'
import type { MessagePings } from '#entities/message'
import type { HtmlNodeDefinition, HtmlFragmentDefinition, HtmlTagDefinition } from '#shared/model/html'
import Ping from '../ui/ping.vue'

function insertPingIntoText(text: string, pingValue: string, pingUser: User): HtmlFragmentDefinition {
  const textParts = text.split('@' + pingValue)
  if (textParts.length === 1) return text

  const result: HtmlFragmentDefinition = []

  for (let i = 0; i < textParts.length; i++) {
    const textPart = textParts[i]
    result.push(textPart)

    if (i !== textParts.length - 1)
      result.push({
        tag: Ping,
        attrs: {
          user: pingUser,
        },
      })
  }

  return result
}

function insertPingsIntoText(text: string, pings: MessagePings): HtmlFragmentDefinition {
  let result: HtmlFragmentDefinition = [text]

  for (const [pingValue, pingUser] of Object.entries(pings)) {
    const newResult: HtmlFragmentDefinition = []

    for (const node of result) {
      if (typeof node !== 'string') continue

      const updatedTextPart = insertPingIntoText(node, pingValue, pingUser)

      if (Array.isArray(updatedTextPart)) {
        newResult.push(...updatedTextPart)
      } else {
        newResult.push(updatedTextPart)
      }
    }

    result = newResult
  }

  return result
}

function insertPingsIntoTag(node: HtmlTagDefinition, pings: MessagePings): HtmlTagDefinition {
  if (node.tag === 'a') return node
  if (!node.content) return node

  const newNode: HtmlTagDefinition = {
    tag: node.tag,
    attrs: node.attrs,
    content: [],
  }

  if (typeof node.content === 'string') {
    newNode.content = insertPingsIntoText(node.content, pings)
  } else if (Array.isArray(node.content)) {
    const newContent: HtmlNodeDefinition[] = []

    for (const nodeChild of node.content) {
      if (!nodeChild) {
        newContent.push(nodeChild)
        continue
      }

      if (typeof nodeChild === 'number') {
        newContent.push(nodeChild)
        continue
      }

      if (typeof nodeChild === 'string') {
        const transformedNodeChild = insertPingsIntoText(nodeChild, pings)

        if (Array.isArray(transformedNodeChild)) {
          newContent.push(...transformedNodeChild)
        } else {
          newContent.push(transformedNodeChild)
        }

        continue
      }

      newContent.push(insertPingsIntoTag(nodeChild, pings))
    }

    newNode.content = newContent
  }

  return newNode
}

export function insertMessagePings(content: HtmlFragmentDefinition, pings: MessagePings): HtmlFragmentDefinition {
  if (!content) return content

  if (typeof content === 'number') return content

  if (typeof content === 'string') {
    return insertPingsIntoText(content, pings)
  }

  if (Array.isArray(content)) {
    const newContent: HtmlNodeDefinition[] = []

    for (const nodeChild of content) {
      if (!nodeChild) {
        newContent.push(nodeChild)
        continue
      }

      if (typeof nodeChild === 'number') {
        newContent.push(nodeChild)
        continue
      }

      if (typeof nodeChild === 'string') {
        const transformedNodeChild = insertPingsIntoText(nodeChild, pings)

        if (Array.isArray(transformedNodeChild)) {
          newContent.push(...transformedNodeChild)
        } else {
          newContent.push(transformedNodeChild)
        }

        continue
      }

      newContent.push(insertPingsIntoTag(nodeChild, pings))
    }

    return newContent
  }

  return insertPingsIntoTag(content, pings)
}
